import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopBar from '../components/TopBar';
import SafeIcon from '../common/SafeIcon';
import { moods, tracks, friends, demoSampleData } from '../data/mockData';

const workflowSteps = [
  { id: 0, title: 'Mood Calibration', desc: 'Select baseline neural state from the mood stations.', icon: 'FiActivity' },
  { id: 1, title: 'Frequency Tuning', desc: 'Adjust EQ and acoustic preferences for optimal resonance.', icon: 'FiSliders' },
  { id: 2, title: 'Sequence Compile', desc: 'Drag and drop tracks to build your sonic path.', icon: 'FiPlusSquare' },
  { id: 3, title: 'Network Broadcast', desc: 'Initialize listening room and broadcast to the network.', icon: 'FiRadio' },
  { id: 4, title: 'Queue Sync', desc: 'Invite friends and sync their playback latency.', icon: 'FiUsers' },
  { id: 5, title: 'Weekly Recap', desc: 'Analyze auditory history and frequency exposure.', icon: 'FiPieChart' }
];

export default function Demo() {
  const [activeStep, setActiveStep] = useState(0);
  const [simState, setSimState] = useState('ideal'); // ideal, loading, success, empty, warning, error
  const [activeMood, setActiveMood] = useState(moods[0].id);
  const [eqValue, setEqValue] = useState(70);

  // Render the interactive mini-components based on active step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Mood Calibration
        return (
          <div className="grid grid-cols-2 gap-3">
            {moods.slice(0, 4).map(mood => (
              <div 
                key={mood.id} 
                onClick={() => setActiveMood(mood.id)}
                className={`p-3 rounded-xl border cursor-pointer transition-all ${activeMood === mood.id ? 'bg-vinyl-700 border-plasma glow-plasma' : 'bg-vinyl-800 border-vinyl-600 hover:border-gray-400'}`}
              >
                <div className={`w-8 h-8 rounded-lg ${mood.color} flex items-center justify-center mb-2`}>
                  <SafeIcon icon={mood.icon} className="text-white" />
                </div>
                <h4 className="text-xs text-white font-medium">{mood.name}</h4>
                <p className="text-[10px] text-gray-500">{mood.frequency}</p>
              </div>
            ))}
          </div>
        );
      case 1: // Frequency Tuning
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Algorithmic Bass</span>
                <span className="text-aqua">{eqValue}%</span>
              </div>
              <input 
                type="range" min="0" max="100" value={eqValue} 
                onChange={(e) => setEqValue(e.target.value)}
                className="w-full h-1 bg-vinyl-700 rounded-full appearance-none cursor-pointer accent-aqua"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Vocal Clarity</span>
                <span className="text-plasma">85%</span>
              </div>
              <input type="range" min="0" max="100" defaultValue="85" className="w-full h-1 bg-vinyl-700 rounded-full appearance-none cursor-pointer accent-plasma" />
            </div>
          </div>
        );
      case 2: // Sequence Compile
        return (
          <div className="space-y-2">
            {tracks.slice(0, 3).map((track, i) => (
              <div key={track.id} className="flex items-center gap-3 p-2 bg-vinyl-800 rounded-lg border border-vinyl-700">
                <SafeIcon icon="FiMenu" className="text-gray-500 cursor-move" />
                <img src={track.cover} alt="" className="w-8 h-8 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{track.title}</p>
                  <p className="text-[10px] text-gray-500 truncate">{track.artist}</p>
                </div>
                <SafeIcon icon="FiMinusCircle" className="text-gray-600 hover:text-plasma cursor-pointer" />
              </div>
            ))}
            <button className="w-full py-2 mt-2 border border-dashed border-vinyl-600 rounded-lg text-xs text-gray-400 hover:text-white hover:border-aqua transition-colors flex items-center justify-center gap-2">
              <SafeIcon icon="FiPlus" /> Add Track
            </button>
          </div>
        );
      case 3: // Network Broadcast
        return (
          <div className="p-4 bg-vinyl-800 rounded-xl border border-plasma/30 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-2">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-plasma opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-plasma"></span>
              </span>
            </div>
            <h4 className="text-sm font-bold text-white mb-1">Neon Drive Session</h4>
            <p className="text-xs text-gray-400 mb-4">Broadcasting to Sector 7G...</p>
            <div className="flex justify-between items-center text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><SafeIcon icon="FiHeadphones" /> 892 Listeners</span>
              <span className="text-aqua font-mono">12ms Latency</span>
            </div>
          </div>
        );
      case 4: // Queue Sync
        return (
          <div className="space-y-3">
            {friends.slice(0, 2).map(friend => (
              <div key={friend.id} className="flex items-center justify-between p-2 bg-vinyl-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <img src={friend.avatar} alt="" className="w-8 h-8 rounded-full border border-vinyl-600" />
                  <div>
                    <h4 className="text-xs text-white">{friend.name}</h4>
                    <p className="text-[10px] text-aqua">In Room</p>
                  </div>
                </div>
                <button className="px-3 py-1 bg-vinyl-700 text-[10px] text-white rounded hover:bg-aqua hover:text-vinyl-900 transition-colors">
                  Sync
                </button>
              </div>
            ))}
          </div>
        );
      case 5: // Weekly Recap
        return (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
            <div className="relative w-24 h-24 rounded-full border-4 border-vinyl-700 border-t-plasma border-r-aqua flex items-center justify-center">
              <SafeIcon icon="FiMusic" className="text-2xl text-white" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Top Frequency</h4>
              <p className="text-xs text-gray-400">142Hz • Cyberpunk City</p>
              <p className="text-lg font-display text-plasma mt-2">42 Hours</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto w-full h-full bg-mesh-pattern pb-40">
      <TopBar title="Interactive Demo" />
      
      <div className="p-6 max-w-7xl mx-auto space-y-12">
        {/* Header Section */}
        <section className="text-center space-y-4 pt-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tighter">
            How The <span className="text-plasma text-glow-plasma">SoundNest</span> Engine Works
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm">
            Experience a realistic user journey for music fans curating themed listening sessions. 
            Explore the workflow below to see how our nodes process, compile, and broadcast sonic pathways.
          </p>
        </section>

        {/* Try This Flow Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon="FiGitCommit" className="text-aqua text-xl" />
            <h3 className="text-xl font-display font-bold text-white">Try this flow</h3>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Steps List */}
            <div className="lg:col-span-5 space-y-3">
              {workflowSteps.map((step, idx) => (
                <button 
                  key={step.id} 
                  onClick={() => setActiveStep(idx)}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-start gap-4 ${
                    activeStep === idx 
                      ? 'bg-vinyl-800 border-aqua shadow-[inset_4px_0_0_#00FFFF]' 
                      : 'bg-vinyl-900/50 border-vinyl-700 hover:bg-vinyl-800 hover:border-gray-600'
                  }`}
                >
                  <div className={`mt-0.5 p-2 rounded-lg ${activeStep === idx ? 'bg-aqua/20 text-aqua' : 'bg-vinyl-700 text-gray-500'}`}>
                    <SafeIcon icon={step.icon} />
                  </div>
                  <div>
                    <h4 className={`text-sm font-bold ${activeStep === idx ? 'text-white' : 'text-gray-400'}`}>
                      {step.id + 1}. {step.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">{step.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Interactive Preview Panel */}
            <div className="lg:col-span-7">
              <div className="glass-panel rounded-2xl h-[400px] flex flex-col relative overflow-hidden border-t-2 border-t-plasma shadow-2xl">
                
                {/* Simulated Device Header */}
                <div className="h-12 bg-vinyl-900 border-b border-vinyl-700 flex items-center px-4 justify-between">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                  <span className="text-[10px] text-gray-500 font-mono tracking-widest uppercase">
                    Preview: {workflowSteps[activeStep].title}
                  </span>
                </div>

                {/* Simulated Content Area */}
                <div className="flex-1 p-6 relative">
                  <AnimatePresence mode="wait">
                    {/* OVERLAYS BASED ON STATE */}
                    {simState === 'loading' && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-vinyl-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-aqua">
                        <SafeIcon icon="FiLoader" className="text-4xl animate-spin mb-4" />
                        <p className="text-sm font-mono tracking-widest">PROCESSING...</p>
                      </motion.div>
                    )}
                    {simState === 'success' && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-green-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-green-400">
                        <SafeIcon icon="FiCheckCircle" className="text-5xl mb-4" />
                        <p className="text-sm font-bold tracking-widest uppercase">Action Successful</p>
                      </motion.div>
                    )}
                    {simState === 'warning' && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-yellow-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-yellow-400">
                        <SafeIcon icon="FiAlertTriangle" className="text-5xl mb-4" />
                        <p className="text-sm font-bold tracking-widest uppercase">High Latency Detected</p>
                      </motion.div>
                    )}
                    {simState === 'error' && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-red-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-red-500">
                        <SafeIcon icon="FiXOctagon" className="text-5xl mb-4" />
                        <p className="text-sm font-bold tracking-widest uppercase">Connection Lost</p>
                      </motion.div>
                    )}
                    {simState === 'empty' && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-vinyl-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-gray-500">
                        <SafeIcon icon="FiInbox" className="text-5xl mb-4 opacity-50" />
                        <p className="text-sm tracking-widest uppercase">No Data Available</p>
                      </motion.div>
                    )}

                    {/* ACTUAL COMPONENT RENDER (IDEAL STATE) */}
                    <motion.div 
                      key={activeStep}
                      initial={{ opacity: 0, scale: 0.95 }} 
                      animate={{ opacity: 1, scale: 1 }} 
                      exit={{ opacity: 0, scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                      className="h-full"
                    >
                      {renderStepContent()}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simulated States Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon="FiLayers" className="text-plasma text-xl" />
            <h3 className="text-xl font-display font-bold text-white">Simulated States</h3>
          </div>
          <div className="glass-panel p-6 rounded-2xl">
            <p className="text-sm text-gray-400 mb-6">Use these controls to force the interactive preview panel above into different operational states.</p>
            <div className="flex flex-wrap gap-4">
              {[
                { id: 'ideal', label: 'Before / Ideal', icon: 'FiPlay', color: 'text-white border-vinyl-600 hover:border-white' },
                { id: 'loading', label: 'In Progress', icon: 'FiLoader', color: 'text-aqua border-aqua/30 hover:border-aqua hover:bg-aqua/10' },
                { id: 'success', label: 'Success', icon: 'FiCheck', color: 'text-green-400 border-green-500/30 hover:border-green-500 hover:bg-green-500/10' },
                { id: 'warning', label: 'Warning', icon: 'FiAlertCircle', color: 'text-yellow-400 border-yellow-500/30 hover:border-yellow-500 hover:bg-yellow-500/10' },
                { id: 'error', label: 'Error', icon: 'FiX', color: 'text-red-500 border-red-500/30 hover:border-red-500 hover:bg-red-500/10' },
                { id: 'empty', label: 'Empty', icon: 'FiInbox', color: 'text-gray-400 border-gray-600 hover:border-gray-400 hover:bg-gray-800' }
              ].map(state => (
                <button
                  key={state.id}
                  onClick={() => setSimState(state.id)}
                  className={`px-4 py-2 rounded-lg border transition-all flex items-center gap-2 text-sm font-medium ${state.color} ${simState === state.id ? 'bg-vinyl-700 ring-2 ring-offset-2 ring-offset-vinyl-900 ring-plasma' : 'bg-transparent'}`}
                >
                  <SafeIcon icon={state.icon} className={state.id === 'loading' ? 'animate-spin' : ''} />
                  {state.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sample Data Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon="FiDatabase" className="text-aqua text-xl" />
            <h3 className="text-xl font-display font-bold text-white">Sample Data</h3>
          </div>
          <div className="glass-panel rounded-2xl overflow-hidden overflow-x-auto border-t-2 border-t-aqua">
            <table className="w-full text-left text-sm text-gray-400">
              <thead className="text-xs text-gray-500 uppercase bg-vinyl-900/80 border-b border-vinyl-700">
                <tr>
                  <th className="px-6 py-4">Session ID</th>
                  <th className="px-6 py-4">Session Name</th>
                  <th className="px-6 py-4">User</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Duration</th>
                  <th className="px-6 py-4">Active Nodes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-vinyl-700/50">
                {demoSampleData.map((row) => (
                  <tr key={row.id} className="hover:bg-vinyl-800/50 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs text-plasma">{row.id}</td>
                    <td className="px-6 py-4 font-medium text-white">{row.session}</td>
                    <td className="px-6 py-4">{row.user}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                        row.status === 'Active' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                        row.status === 'Completed' ? 'bg-aqua/10 text-aqua border border-aqua/20' :
                        row.status === 'Processing' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' :
                        'bg-red-500/10 text-red-500 border border-red-500/20'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs">{row.duration}</td>
                    <td className="px-6 py-4 text-white font-bold">{row.nodes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Subtle Credit */}
        <div className="pt-12 pb-6 text-center border-t border-vinyl-700/50">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest font-display">
            Made by LingLink AI and DigLab AI
          </p>
        </div>
      </div>
    </div>
  );
}